import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  FaBullseye,
  FaPaperPlane,
  FaUserCircle,
  FaSearch,
  FaEllipsisV,
  FaSmile,
  FaPlus,
  FaMicrophone,
  FaSyncAlt,
  FaTerminal,
  FaHistory,
  FaCog,
  FaCommentDots,
  FaMagic,
  FaGlobe,
  FaFlask,
  FaExclamationTriangle,
  FaRocket,
  FaStar,
  FaChevronDown,
  FaEllipsisH
} from "react-icons/fa";
import { askAI } from "../utils/gemini";
import { ref, onValue, push, set } from "firebase/database";
import { db } from "../firebase";
import { useLocation, useNavigate } from "react-router-dom";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import LeftBar from "./LeftBar";
import "./AIChatWindow.css";
import "./SamparkPortal.css";
import "./Chat.css"; // Ensure standard chat styles for LeftBar

function AIChatWindow({ currentUser, isNested = false }) {
  const location = useLocation();
  const navigate = useNavigate();
  const isStandalone = location.pathname.toUpperCase() === "/SAMPARKAI";
  
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isThinking, setIsThinking] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [selectedModel, setSelectedModel] = useState("free");
  const [showModelMenu, setShowModelMenu] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const handleModelChange = (val) => {
    setShowModelMenu(false);
    if (val === "plus" || val === "pro") {
      navigate('/pricing');
      setSelectedModel("free");
    } else {
      setSelectedModel(val);
    }
  };
  const [theme, setTheme] = useState("dark");

  useEffect(() => {
    const savedTheme = localStorage.getItem("theme");
    let initialTheme = "dark";
    if (savedTheme) {
      initialTheme = savedTheme;
    } else {
      const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
      initialTheme = prefersDark ? "dark" : "light";
    }
    setTheme(initialTheme);
    document.documentElement.setAttribute("data-theme", initialTheme);
  }, []);

  const toggleTheme = () => {
    const newTheme = theme === "dark" ? "light" : "dark";
    setTheme(newTheme);
    document.documentElement.setAttribute("data-theme", newTheme);
    localStorage.setItem("theme", newTheme);
  };

  // Suggestions for empty state
  const suggestions = [
    "🇮🇳 Latest in Bharat Tech?",
    "Ask Sampark AI",
    "📝 Summarize recent threads"
  ];

  useEffect(() => {
    if (!currentUser) return;
    const aiRef = ref(db, `ai_chats/${currentUser.uid}`);
    const unsubscribe = onValue(aiRef, (snapshot) => {
      const data = snapshot.val();
      if (!data) {
        setMessages([]);
        return;
      }
      const msgList = Object.entries(data).map(([id, val]) => ({ id, ...val }));
      setMessages(msgList);
    });
    return () => unsubscribe();
  }, [currentUser]);

  useEffect(() => {
    requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      const messagesContainer = scrollRef.current.closest('.sai-messages');
      if (messagesContainer) {
        messagesContainer.scrollTop = messagesContainer.scrollHeight;
      }
    }
  }, [messages, isThinking, isTyping]);

  const handleSend = async (e, forcedText = null) => {
    if (e) e.preventDefault();
    const textToSend = forcedText || input;
    
    if (!textToSend.trim() || isThinking || isTyping) return;

    // Handle Commands
    if (textToSend.toLowerCase() === "/clear") {
      handleClearChat();
      setInput("");
      return;
    }

    const userMsg = {
      text: textToSend,
      sender: "user",
      timestamp: Date.now()
    };

    setInput("");
    const aiRef = ref(db, `ai_chats/${currentUser.uid}`);
    push(aiRef, userMsg);

    setIsThinking(true);

    // Call Gemini with high-performance history
    const history = messages.slice(-10).map(m => ({
      role: m.sender === "ai" ? "model" : "user",
      parts: [{ text: m.text }]
    }));

    try {
      const aiResponse = await askAI(textToSend, history);
      setIsThinking(false);
      
      // Simulate Typing Effect for Premium Feel
      await simulateTyping(aiResponse, aiRef);
    } catch (error) {
      console.error("AI Error:", error);
      setIsThinking(false);
      push(aiRef, {
        text: "I encountered a connectivity shield block. Retrying via REST fallback...",
        sender: "ai",
        isError: true,
        timestamp: Date.now()
      });
    }
  };

  const simulateTyping = (fullText, aiRef) => {
    return new Promise((resolve) => {
      setIsTyping(true);
      // For the UI, we just push the whole message to Firebase, 
      // but we could also do a local "typing" state. 
      // To keep Firebase as the source of truth, we push immediately 
      // but toggle the typing state locally for visual resonance.
      
      const aiMsg = {
        text: fullText,
        sender: "ai",
        timestamp: Date.now()
      };
      
      setTimeout(() => {
        push(aiRef, aiMsg);
        setIsTyping(false);
        resolve();
      }, 600); // Artificial "Neural Analysis" delay
    });
  };

  const handleClearChat = async () => {
    if (window.confirm("Are you sure you want to clear your AI workspace history?")) {
      const aiRef = ref(db, `ai_chats/${currentUser.uid}`);
      await set(aiRef, null);
      setShowMenu(false);
    }
  };

  const welcomeMessage = useMemo(() => (
    <div className="sai-welcome-screen">
      <div className="sai-welcome-center">
        <FaBullseye className="sai-welcome-logo aiPulse" />
        <h1 className="sai-welcome-title">Hello, how can I help you today?</h1>
        <p className="sai-welcome-subtitle">I'm Sampark AI, your contextual intelligence partner.</p>
        
        <div className="sai-suggestion-grid">
          {suggestions.map((s, i) => (
            <div 
              key={i} 
              className="sai-suggestion-card" 
              onClick={() => handleSend(null, s)}
            >
              <div className="sai-card-icon">{i === 0 ? <FaMagic /> : i === 1 ? <FaBullseye /> : <FaHistory />}</div>
              <span>{s}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  ), [currentUser, suggestions]);

  return (
    <div className={`sai-portal-wrapper ${isStandalone ? "sai-standalone" : "sai-nested"}`}>
      {/* 🟢 PERSISTENT APP SIDEBAR */}
      <LeftBar 
        currentUser={currentUser} 
        theme={theme} 
        toggleTheme={toggleTheme} 
        activeTab="ai" 
        setActiveTab={() => (window.location.href = "/chat")} 
      />



      <div className="sai-container">
        {/* HEADER */}
        <div className="sai-header">
          <div className="sai-header-brand" style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <h3>Sampark AI <span className="sai-version-pill">v3.0</span></h3>
          </div>
          <div className="sai-header-user">
             <FaUserCircle size={28} />
          </div>
        </div>

        {/* MESSAGES / WORKSPACE */}
        <div className="sai-messages">
          <div className="sai-workspace">
            {messages.length === 0 && welcomeMessage}
            
            {messages.map((m) => {
              const isSent = m.sender === "user";
              return (
                <div
                  key={m.id}
                  className={`sai-msg-row ${isSent ? "sai-user-row" : "sai-ai-row"}`}
                >
                  <div className="sai-msg-avatar">
                    {isSent ? <FaUserCircle /> : <FaBullseye className="aiPulse" />}
                  </div>
                  <div className="sai-msg-content">
                    <ReactMarkdown 
                      remarkPlugins={[remarkGfm]}
                      components={{
                        code({node, inline, className, children, ...props}) {
                          const match = /language-(\w+)/.exec(className || '')
                          return !inline && match ? (
                            <SyntaxHighlighter
                              children={String(children).replace(/\n$/, '')}
                              style={vscDarkPlus}
                              language={match[1]}
                              PreTag="div"
                              {...props}
                            />
                          ) : (
                            <code className={className} {...props}>
                              {children}
                            </code>
                          )
                        }
                      }}
                    >
                      {m.text.replace("ERROR_PREFIX", "")}
                    </ReactMarkdown>
                    {m.text.includes("ERROR_PREFIX") && (
                      <div style={{ color: '#ff4d4f', display: 'flex', alignItems: 'center', gap: '8px', marginTop: '10px' }}>
                        <FaExclamationTriangle />
                        <span>System Error Detected</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            {isThinking && (
              <div className="sai-msg-row sai-ai-row sai-thinking">
                <div className="sai-msg-avatar">
                  <FaBullseye className="aiPulse" />
                </div>
                <div className="sai-msg-content">
                  <span className="sai-thinking-text">Thinking...</span>
                </div>
              </div>
            )}
            
            <div ref={scrollRef} style={{ height: '100px' }}></div>
          </div>
        </div>

        {/* ASSISTANT-UI STYLE INPUT BAR */}
        <div className="sai-input-wrapper">
          <form onSubmit={handleSend} className="acme-input-box">
            <input
              ref={inputRef}
              type="text"
              placeholder="Ask anything..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              className="acme-input-text"
            />
            <div className="acme-input-toolbar">
              <div className="acme-toolbar-left">
                <div className="custom-model-dropdown-container">
                  <button 
                    type="button" 
                    className="sai-model-selector acme-model-select"
                    onClick={() => setShowModelMenu(!showModelMenu)}
                  >
                    {selectedModel === 'free' ? 'Gemma Free' : selectedModel === 'plus' ? <><FaStar style={{color: '#fadb14'}}/> Plus</> : <><FaRocket style={{color: '#ff4d4f'}}/> Pro</>}
                    <FaChevronDown style={{ fontSize: '10px', marginLeft: '6px' }} />
                  </button>

                  {showModelMenu && (
                    <div className="custom-model-menu">
                      <div className={`custom-model-option ${selectedModel === 'free' ? 'active' : ''}`} onClick={() => handleModelChange('free')}>
                        Gemma Free
                      </div>
                      <div className="custom-model-option premium" onClick={() => handleModelChange('plus')}>
                        <FaStar style={{color: '#fadb14'}}/> Plus
                      </div>
                      <div className="custom-model-option premium" onClick={() => handleModelChange('pro')}>
                        <FaRocket style={{color: '#ff4d4f'}}/> Pro
                      </div>
                    </div>
                  )}
                </div>
                <button type="button" className="acme-btn-icon"><FaPlus /></button>
                <button type="button" className="acme-btn-icon"><FaGlobe /></button>
                <button type="button" className="acme-btn-pill"><FaFlask /> Research</button>
                <button type="button" className="acme-btn-icon"><FaEllipsisH /></button>
              </div>
              <div className="acme-toolbar-right">
                <button type="button" className="acme-btn-icon"><FaMicrophone /></button>
                <button type="submit" className="acme-submit-btn" disabled={!input.trim()}>
                  <FaPaperPlane className={isThinking ? "aiPulse" : ""} />
                </button>
              </div>
            </div>
          </form>
          <div className="acme-disclaimer">
            AI responses can be inaccurate or misleading. <a href="/terms" target="_blank" rel="noopener noreferrer">Learn more</a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AIChatWindow;
